import { useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { ScheduleCellAssignment, ScheduleRow } from '../../lib/types';
import { buildBars, laneCount, type ScheduleBar as Bar } from '../../lib/scheduleBars';
import { ScheduleBar } from './ScheduleBar';
import type { Density } from './ScheduleFilters';

interface ScheduleGridProps {
  rows: ScheduleRow[];
  days: string[];
  density: Density;
  onCellClick: (assignment: ScheduleCellAssignment, personId: number) => void;
  highlightPersonId?: number;
}

const PERSON_COL_WIDTH = 192;
const MIN_DAY_COL_WIDTH = 60;   // narrower than this and the date header collides with itself
// Was 30 when a chip held one line. A bar carries the project *and* its location, wrapping to a
// second line when it needs to, so it's ~55% taller.
const BAR_HEIGHT = 46;
const BAR_GAP = 4;               // vertical gap between stacked (overlapping) bars
const ROW_PADDING = 8;           // total vertical padding inside a row (4 top + 4 bottom)
const MIN_ROW_HEIGHT = BAR_HEIGHT + ROW_PADDING;

function rowHeightForLanes(lanes: number): number {
  if (lanes <= 1) return MIN_ROW_HEIGHT;
  return lanes * BAR_HEIGHT + (lanes - 1) * BAR_GAP + ROW_PADDING;
}

function formatDayHeader(dateStr: string) {
  const d = new Date(dateStr + 'T00:00:00');
  return {
    month: d.toLocaleString('default', { month: 'short' }),
    day: d.getDate(),
    weekday: d.toLocaleString('default', { weekday: 'short' }),
  };
}

function isWeekend(dateStr: string): boolean {
  const d = new Date(dateStr + 'T00:00:00');
  const dow = d.getDay();
  return dow === 0 || dow === 6;
}

function isToday(dateStr: string): boolean {
  return dateStr === new Date().toISOString().slice(0, 10);
}

/**
 * Pick the per-day column width based on density and the available container width.
 *  - Day:   1 column, fills the entire remaining width (full-width single day).
 *  - Week:  7 columns at exactly 1/7 of the remaining width (no horizontal scroll).
 *  - Month: 7-day-equivalent width (same as Week) but with ~30 columns, so the total
 *           exceeds the container and the user scrolls horizontally.
 * If the container is too narrow to honor the 1/7 split, falls back to a minimum
 * width per column and lets the whole grid scroll horizontally.
 */
function dayColWidthFor(density: Density, availableWidth: number): number {
  if (availableWidth <= 0) return MIN_DAY_COL_WIDTH;
  if (density === 'day') return Math.max(availableWidth, MIN_DAY_COL_WIDTH);
  // Week + Month both target 1/7 of the available width per column.
  return Math.max(availableWidth / 7, MIN_DAY_COL_WIDTH);
}

export function ScheduleGrid({ rows, days, density, onCellClick, highlightPersonId }: ScheduleGridProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setContainerWidth(el.clientWidth);
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setContainerWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Bars and row heights are derived once per data change, not per render: lane packing is
  // O(n log n) per row and the virtualizer asks for heights constantly while scrolling.
  // The result is deterministic from props, so estimateSize can use it directly without
  // measureElement after render.
  const barsByRow = useMemo(
    () => rows.map((row) => buildBars(row.cells, days)),
    [rows, days],
  );
  const rowHeights = useMemo(
    () => barsByRow.map((bars) => rowHeightForLanes(laneCount(bars))),
    [barsByRow],
  );

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => containerRef.current,
    estimateSize: (index) => rowHeights[index] ?? MIN_ROW_HEIGHT,
    overscan: 5,
  });

  const availableWidth = Math.max(0, containerWidth - PERSON_COL_WIDTH);
  const dayColWidth = dayColWidthFor(density, availableWidth);
  const totalWidth = PERSON_COL_WIDTH + days.length * dayColWidth;

  return (
    <div
      ref={containerRef}
      className="overflow-auto flex-1 min-h-0 bg-white"
    >
      <div style={{ width: totalWidth, position: 'relative' }}>
        {/* Sticky date header */}
        <div
          className="sticky top-0 z-10 bg-white border-b border-slate-400 flex"
          style={{ width: totalWidth }}
        >
          <div
            className="sticky left-0 z-20 bg-white border-r border-slate-400"
            style={{ width: PERSON_COL_WIDTH, minWidth: PERSON_COL_WIDTH }}
          />
          {days.map((d) => {
            const { month, day, weekday } = formatDayHeader(d);
            const today = isToday(d);
            return (
              <div
                key={d}
                role="columnheader"
                className={`text-center border-r border-slate-300 shrink-0 ${today ? 'border-b-2 border-b-brand-orange' : ''}`}
                style={{ width: dayColWidth, minWidth: dayColWidth }}
              >
                <div className="text-[10px] text-slate-400 leading-none pt-1">{weekday} · {month}</div>
                <div
                  className={`text-xs leading-none pb-1 ${today ? 'font-bold text-brand-orange' : 'text-slate-600'}`}
                >
                  {day}
                </div>
              </div>
            );
          })}
        </div>

        {/* Virtualized rows */}
        <div
          role="grid"
          aria-label="Schedule grid"
          style={{ height: rowVirtualizer.getTotalSize(), position: 'relative' }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const row = rows[virtualRow.index];
            const rowHeight = rowHeights[virtualRow.index];
            const bars: Bar[] = barsByRow[virtualRow.index] ?? [];
            const isHighlighted = highlightPersonId != null && row.personId === highlightPersonId;
            const rowBg = isHighlighted ? 'bg-brand-blue/10' : '';
            return (
              <div
                key={row.personId}
                className={`absolute flex border-b border-slate-300 ${rowBg}`}
                style={{
                  top: virtualRow.start,
                  height: rowHeight,
                  width: totalWidth,
                }}
              >
                {/* Person cell */}
                <div
                  role="rowheader"
                  className={`sticky left-0 z-10 border-r border-slate-400 flex flex-col justify-center px-2 shrink-0 ${isHighlighted ? 'bg-brand-blue/10' : 'bg-white'}`}
                  style={{ width: PERSON_COL_WIDTH, minWidth: PERSON_COL_WIDTH }}
                >
                  <span className="text-sm font-medium text-brand-charcoal truncate">{row.displayName}</span>
                  {row.region && (
                    <span className="text-xs text-brand-charcoal/60 truncate">{row.region}</span>
                  )}
                </div>

                {/* Two layers: the day grid (weekend shading + column rules), then the bars
                    floating over it. A bar has to cross column boundaries to read as one
                    continuous run, so it can't live inside a day cell. */}
                <div className="relative shrink-0" style={{ width: days.length * dayColWidth }}>
                  <div className="absolute inset-0 flex" aria-hidden="true">
                    {days.map((d) => {
                      const weekend = isWeekend(d);
                      return (
                        <div
                          key={d}
                          className={`border-r border-slate-300 shrink-0 ${weekend ? 'bg-slate-50' : ''} ${isHighlighted && !weekend ? 'bg-brand-blue/10' : ''}`}
                          style={{ width: dayColWidth, minWidth: dayColWidth }}
                        />
                      );
                    })}
                  </div>

                  {bars.map((bar) => (
                    <div
                      key={bar.assignment.assignmentId}
                      className="absolute"
                      style={{
                        // Inset by 1px each side so adjacent runs read as two bars rather than
                        // one; a bar clipped by the window edge keeps its full width there so it
                        // runs flush to the boundary.
                        left: bar.startIndex * dayColWidth + (bar.continuesBefore ? 0 : 1),
                        width:
                          (bar.endIndex - bar.startIndex + 1) * dayColWidth -
                          (bar.continuesBefore ? 0 : 1) -
                          (bar.continuesAfter ? 0 : 1),
                        top: ROW_PADDING / 2 + bar.lane * (BAR_HEIGHT + BAR_GAP),
                        height: BAR_HEIGHT,
                      }}
                    >
                      <ScheduleBar
                        assignment={bar.assignment}
                        continuesBefore={bar.continuesBefore}
                        continuesAfter={bar.continuesAfter}
                        onClick={() => onCellClick(bar.assignment, row.personId)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
